import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { RideService } from '../rideService';
import { CityService, City } from '../../cities/cityService';
import { UserService } from '../../users/userService';
import { ListUserDto, AddressDto } from '../../users/dto/listUserDto';
import { ListRideDto } from '../dto/listRideDto';
import { RestResponse } from '../../../shared/RestResponse';
import { Pagination } from '../../../shared/Pagination';
import { GetRideDto } from '../dto/getRideDto';

@Component({
  selector: 'app-list-ride',
  templateUrl: './list-ride.html',
  imports: [CommonModule, FormsModule, TableModule, ButtonModule, InputTextModule, TagModule, TooltipModule, DialogModule, SelectModule, DatePickerModule, ConfirmDialogModule]
})
export class ListRideComponent implements OnInit, OnDestroy {
  rides: ListRideDto[] = [];
  driverId: number | null = null;
  fromCityId: number | null = null;
  toCityId: number | null = null;
  status: string | null = null;
  startDate: Date | null = null;
  endDate: Date | null = null;
  
  // Listas para os filtros
  drivers: any[] = [];
  statusOptions = [
    { label: 'Pendente', value: 'pendente' },
    { label: 'Iniciada', value: 'Iniciada' },
    { label: 'Concluída', value: 'concluída' },
    { label: 'Cancelada', value: 'cancelada' }
  ];
  
  // Propriedades para o modal de clientes
  clientsModalVisible: boolean = false;
  addClientModalVisible: boolean = false;
  rideClients: any[] = [];
  availableClients: any[] = [];
  selectedClientId: number | null = null;
  currentRideId: number | null = null;
  cities: City[] = [];
  modalFromCities: any[] = [];
  modalToCities: any[] = [];
  isLoadingModalFromCities: boolean = false;
  isLoadingModalToCities: boolean = false;
  selectedClientAddresses: AddressDto[] = [];
  
  private modalFromCitySearchSubject = new Subject<string>();
  private modalToCitySearchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  
  // Modo de entrada de endereços (true = usar existente, false = inserir manualmente)
  useExistingFromAddress: boolean = false;
  useExistingToAddress: boolean = false;
  selectedFromAddressIndex: number | null = null;
  selectedToAddressIndex: number | null = null;
  
  // Estrutura para dados de endereço do cliente
  clientAddress = {
    fromAddress: {
      addressName: '',
      number: '',
      district: '',
      cityId: null as number | null,
      zipCode: ''
    },
    toAddress: {
      addressName: '',
      number: '',
      district: '',
      cityId: null as number | null,
      zipCode: ''
    }
  };

  constructor(
    private rideService: RideService,
    private cityService: CityService,
    private userService: UserService,
    private router: Router,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadRides();
    this.loadCities();
    this.loadDrivers();
    
    // Configurar busca de cidade de origem do modal com debounce
    this.modalFromCitySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(search => {
        this.isLoadingModalFromCities = true;
        return this.cityService.list(search || '');
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: RestResponse<City[]>) => {
        this.modalFromCities = (response.data || []).map(city => ({
          ...city,
          displayName: `${city.name} - ${city.stateName}`
        }));
        this.isLoadingModalFromCities = false;
      },
      error: (error) => {
        console.error('Erro ao buscar cidades de origem do modal:', error);
        this.modalFromCities = [];
        this.isLoadingModalFromCities = false;
      }
    });
    
    // Configurar busca de cidade de destino do modal com debounce
    this.modalToCitySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(search => {
        this.isLoadingModalToCities = true;
        return this.cityService.list(search || '');
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: RestResponse<City[]>) => {
        this.modalToCities = (response.data || []).map(city => ({
          ...city,
          displayName: `${city.name} - ${city.stateName}`
        }));
        this.isLoadingModalToCities = false;
      },
      error: (error) => {
        console.error('Erro ao buscar cidades de destino do modal:', error);
        this.modalToCities = [];
        this.isLoadingModalToCities = false;
      }
    });
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadRides(): void {
    const startDateStr = this.startDate ? this.startDate.toISOString().split('T')[0] : null;
    const endDateStr = this.endDate ? this.endDate.toISOString().split('T')[0] : null;
    
    this.rideService.list('', this.driverId, this.fromCityId, this.toCityId, this.status, startDateStr, endDateStr, 1).subscribe((response: RestResponse<ListRideDto[]>) => {
      this.rides = response.data;
    });
  }

  loadDrivers(): void {
    // Assumindo que roleId 4 é para motoristas (DRIVER)
    this.userService.list('', 1, undefined, undefined, undefined, [4]).subscribe({
      next: (response: RestResponse<any[]>) => {
        this.drivers = response.data;
      },
      error: (error) => {
        console.error('Erro ao carregar motoristas:', error);
        this.drivers = [];
      }
    });
  }

  loadCities(): void {
    this.cityService.list().subscribe({
      next: (response: RestResponse<City[]>) => {
        this.cities = response.data;
      },
      error: (error) => {
        console.error('Erro ao carregar cidades:', error);
        this.cities = [];
      }
    });
  }

  onStartDateChange(date: Date | null): void {
    this.startDate = date;
    this.loadRides();
  }

  onEndDateChange(date: Date | null): void {
    this.endDate = date;
    this.loadRides();
  }

  onDriverChange(driverId: number | null): void {
    this.driverId = driverId;
    this.loadRides();
  }

  onFromCityChange(fromCityId: number | null): void {
    this.fromCityId = fromCityId;
    this.loadRides();
  }

  onToCityChange(toCityId: number | null): void {
    this.toCityId = toCityId;
    this.loadRides();
  }

  onStatusChange(status: string | null): void {
    this.status = status;
    this.loadRides();
  }

  clearFilters(): void {
    this.driverId = null;
    this.fromCityId = null;
    this.toCityId = null;
    this.status = null;
    this.startDate = null;
    this.endDate = null;
    this.loadRides();
  }

  editRide(id: number): void {
    this.router.navigate(['/rides/edit', id]);
  }

  deleteRide(id: number): void {
    this.confirmationService.confirm({
      message: 'Tem certeza que deseja excluir esta corrida?',
      header: 'Confirmação',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.rideService.delete(id).subscribe({
          next: () => {
            this.loadRides();
          },
          error: (error) => {
            console.error('Erro ao excluir corrida:', error);
          }
        });
      }
    });
  }

  navigateToCreate(): void {
    this.router.navigate(['/rides/create']);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  // Métodos para gerenciar clientes da viagem
  showRideClients(rideId: number): void {
    this.currentRideId = rideId;
    this.clientsModalVisible = true;
    this.loadRideClients(rideId);
  }

  loadRideClients(rideId: number): void {
    this.rideService.getById(rideId).subscribe({
      next: (response: RestResponse<GetRideDto>) => {
        this.rideClients = response.data.customers || [];
      },
      error: (error) => {
        console.error('Erro ao carregar clientes da viagem:', error);
        this.rideClients = [];
      }
    });
  }

  showAddClientDialog(): void {
    this.addClientModalVisible = true;
    this.selectedClientId = null;
    this.selectedClientAddresses = [];
    this.useExistingFromAddress = false;
    this.useExistingToAddress = false;
    this.selectedFromAddressIndex = null;
    this.selectedToAddressIndex = null;
    this.resetClientAddress();
    this.loadAvailableClients();
    // Carregar cidades iniciais para o modal
    this.loadModalCities();
  }
  
  private loadModalCities(): void {
    this.isLoadingModalFromCities = true;
    this.isLoadingModalToCities = true;
    this.cityService.list().subscribe({
      next: (response: RestResponse<City[]>) => {
        const cities = response.data.map(city => ({
          ...city,
          displayName: `${city.name} - ${city.stateName}`
        }));
        this.modalFromCities = [...cities];
        this.modalToCities = [...cities];
        this.isLoadingModalFromCities = false;
        this.isLoadingModalToCities = false;
      },
      error: (error) => {
        console.error('Erro ao carregar cidades do modal:', error);
        this.modalFromCities = [];
        this.modalToCities = [];
        this.isLoadingModalFromCities = false;
        this.isLoadingModalToCities = false;
      }
    });
  }
  
  onModalFromCityFilter(event: any): void {
    const search = event.filter || '';
    this.modalFromCitySearchSubject.next(search);
  }
  
  onModalToCityFilter(event: any): void {
    const search = event.filter || '';
    this.modalToCitySearchSubject.next(search);
  }

  resetClientAddress(): void {
    this.clientAddress = {
      fromAddress: {
        addressName: '',
        number: '',
        district: '',
        cityId: null,
        zipCode: ''
      },
      toAddress: {
        addressName: '',
        number: '',
        district: '',
        cityId: null,
        zipCode: ''
      }
    };
  }

  onClientSelected(): void {
    if (this.selectedClientId) {
      this.loadClientAddresses(this.selectedClientId);
    } else {
      this.selectedClientAddresses = [];
      this.resetClientAddress();
    }
  }

  loadClientAddresses(clientId: number): void {
    this.userService.getById(clientId).subscribe({
      next: (response: RestResponse<ListUserDto>) => {
        this.selectedClientAddresses = response.data.addresses || [];
        // Se houver endereços, sugere usar existente
        if (this.selectedClientAddresses.length > 0) {
          this.useExistingFromAddress = true;
          this.useExistingToAddress = true;
          this.selectedFromAddressIndex = 0;
          this.selectedToAddressIndex = 0;
          this.applySelectedFromAddress();
          this.applySelectedToAddress();
        } else {
          this.useExistingFromAddress = false;
          this.useExistingToAddress = false;
          this.resetClientAddress();
        }
      },
      error: (error) => {
        console.error('Erro ao carregar endereços do cliente:', error);
        this.selectedClientAddresses = [];
        this.useExistingFromAddress = false;
        this.useExistingToAddress = false;
        this.resetClientAddress();
      }
    });
  }

  onFromAddressModeChange(): void {
    if (this.useExistingFromAddress) {
      if (this.selectedFromAddressIndex !== null && this.selectedClientAddresses.length > 0) {
        this.applySelectedFromAddress();
      }
    } else {
      this.resetFromAddress();
    }
  }

  onToAddressModeChange(): void {
    if (this.useExistingToAddress) {
      if (this.selectedToAddressIndex !== null && this.selectedClientAddresses.length > 0) {
        this.applySelectedToAddress();
      }
    } else {
      this.resetToAddress();
    }
  }

  onFromAddressSelected(): void {
    if (this.selectedFromAddressIndex !== null) {
      this.applySelectedFromAddress();
    }
  }

  onToAddressSelected(): void {
    if (this.selectedToAddressIndex !== null) {
      this.applySelectedToAddress();
    }
  }

  applySelectedFromAddress(): void {
    if (this.selectedFromAddressIndex !== null && this.selectedClientAddresses[this.selectedFromAddressIndex]) {
      const address = this.selectedClientAddresses[this.selectedFromAddressIndex];
      this.clientAddress.fromAddress = {
        addressName: address.addressName,
        number: address.number || '',
        district: address.district,
        cityId: address.cityId as number | null,
        zipCode: address.zipCode
      };
    }
  }

  applySelectedToAddress(): void {
    if (this.selectedToAddressIndex !== null && this.selectedClientAddresses[this.selectedToAddressIndex]) {
      const address = this.selectedClientAddresses[this.selectedToAddressIndex];
      this.clientAddress.toAddress = {
        addressName: address.addressName,
        number: address.number || '',
        district: address.district,
        cityId: address.cityId as number | null,
        zipCode: address.zipCode
      };
    }
  }

  resetFromAddress(): void {
    this.clientAddress.fromAddress = {
      addressName: '',
      number: '',
      district: '',
      cityId: null,
      zipCode: ''
    };
  }

  resetToAddress(): void {
    this.clientAddress.toAddress = {
      addressName: '',
      number: '',
      district: '',
      cityId: null,
      zipCode: ''
    };
  }

  getAddressDisplay(address: AddressDto): string {
    return `${address.addressName}${address.number ? ', ' + address.number : ''}, ${address.district}`;
  }

  getAddressOptions(): any[] {
    return this.selectedClientAddresses.map((address, index) => ({
      label: this.getAddressDisplay(address),
      value: index,
      address: address
    }));
  }

  loadAvailableClients(): void {
    // Busca clientes com roleId 3
    this.userService.list('', 1, undefined, undefined, undefined, [3]).subscribe({
      next: (response: RestResponse<any[]>) => {
        this.availableClients = response.data;
      },
      error: (error) => {
        console.error('Erro ao carregar clientes:', error);
        this.availableClients = [];
      }
    });
  }

  addClientToRide(): void {
    if (this.selectedClientId && this.currentRideId && this.isFormValid()) {
      this.rideService.addClientToRide(this.currentRideId, this.selectedClientId, this.clientAddress).subscribe({
        next: () => {
          this.addClientModalVisible = false;
          this.loadRideClients(this.currentRideId!);
          this.selectedClientId = null;
          this.resetClientAddress();
        },
        error: (error) => {
          console.error('Erro ao adicionar cliente à corrida:', error);
        }
      });
    }
  }

  isFormValid(): boolean {
    if (!this.selectedClientId) {
      return false;
    }

    // Validação do endereço de origem
    const fromAddressValid = this.useExistingFromAddress
      ? this.selectedFromAddressIndex !== null
      : !!(
          this.clientAddress.fromAddress.addressName &&
          this.clientAddress.fromAddress.district &&
          this.clientAddress.fromAddress.cityId &&
          this.clientAddress.fromAddress.zipCode
        );

    // Validação do endereço de destino
    const toAddressValid = this.useExistingToAddress
      ? this.selectedToAddressIndex !== null
      : !!(
          this.clientAddress.toAddress.addressName &&
          this.clientAddress.toAddress.district &&
          this.clientAddress.toAddress.cityId &&
          this.clientAddress.toAddress.zipCode
        );

    return fromAddressValid && toAddressValid;
  }

  removeClientFromRide(clientId: number): void {
    this.confirmationService.confirm({
      message: 'Tem certeza que deseja remover este cliente da corrida?',
      header: 'Confirmação',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        if (this.currentRideId) {
          this.rideService.removeClientFromRide(this.currentRideId, clientId).subscribe({
            next: () => {
              this.loadRideClients(this.currentRideId!);
            },
            error: (error) => {
              console.error('Erro ao remover cliente da corrida:', error);
            }
          });
        }
      }
    });
  }

  cancelAddClient(): void {
    this.addClientModalVisible = false;
    this.selectedClientId = null;
    this.resetClientAddress();
  }

  getStatusSeverity(status: string): "success" | "info" | "warn" | "secondary" | "contrast" | "danger" | null | undefined {
    switch (status?.toLowerCase()) {
      case 'pendente':
        return 'secondary';
      case 'Iniciada':
        return 'info';
      case 'concluída':
        return 'success';
      case 'cancelada':
      case 'cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  }
}
